import { Suspense, createContext, lazy, useContext, useMemo, useState, type ReactNode } from 'react';
import { Dialog } from 'radix-ui';

/**
 * المحرّر يُؤجَّل: لا يُصيَّر إلا داخل اللوحة المفتوحة، فتبعياته (مطابقة المشاريع
 * والأوامر والحفظ) لا محل لها في الحزمة الابتدائية — وميزانية §7 تُقاس على أول
 * تحميل لا على ما بعده. `Suspense` بمساحة محجوزة كي لا ترتد اللوحة عند الوصول.
 */
const CaptureComposer = lazy(() =>
  import('./CaptureComposer').then((m) => ({ default: m.CaptureComposer })),
);


import { CaptureTriggerContext, type CaptureTriggerValue } from '../../app/shell';
import { InkTrailProvider } from './ink-trail';
import { ToastProvider } from '../../app/toast';
import { useIsDesktop, useKeyboardInset } from '../../lib/viewport';

/**
 * سطح الالتقاط الكوني (§6.4). يغلّف شجرة التطبيق فيستبدل السلوك الافتراضي لـ
 * `CaptureTriggerContext` في القشرة (الانتقال إلى `/capture`) بلوحة تُفتح **فوق أي
 * شاشة**: CapturePalette على الديسكتوب وCaptureSheet على الجوال.
 *
 * قواعد ملزمة هنا:
 * - اللوحة تُفتح **في وضع الالتقاط مباشرة** — لا قائمة أوامر أولًا ولا حقل إلزامي.
 * - **Escape لا يفقد النص:** المسوّدة تعيش في هذا المكوّن لا في الحوار، فإغلاق
 *   الحوار لا يهدمها، وهي نفسها مسوّدة شاشة `/capture` (نص واحد لا نسختان).
 */

interface CaptureSurfaceValue extends CaptureTriggerValue {
  readonly closeCapture: () => void;
  readonly isOpen: boolean;
  readonly draft: string;
  readonly setDraft: (text: string) => void;
}

const CaptureSurfaceContext = createContext<CaptureSurfaceValue | null>(null);

export function useCaptureSurface(): CaptureSurfaceValue {
  const value = useContext(CaptureSurfaceContext);
  if (value === null) throw new Error('useCaptureSurface خارج CaptureSurface');
  return value;
}

export function CaptureSurface({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <InkTrailProvider>
        <CaptureSurfaceRoot>{children}</CaptureSurfaceRoot>
      </InkTrailProvider>
    </ToastProvider>
  );
}

function CaptureSurfaceRoot({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const value = useMemo<CaptureSurfaceValue>(
    () => ({
      openCapture: () => setIsOpen(true),
      closeCapture: () => setIsOpen(false),
      isOpen,
      draft,
      setDraft,
    }),
    [isOpen, draft],
  );

  const trigger = useMemo<CaptureTriggerValue>(() => ({ openCapture: () => setIsOpen(true) }), []);

  return (
    <CaptureSurfaceContext.Provider value={value}>
      <CaptureTriggerContext.Provider value={trigger}>
        {children}
        <CaptureDialog
          open={isOpen}
          onOpenChange={setIsOpen}
          draft={draft}
          onDraftChange={setDraft}
        />
      </CaptureTriggerContext.Provider>
    </CaptureSurfaceContext.Provider>
  );
}

const CONTENT_CLASS = [
  // الجوال: ورقة تصعد من الأسفل بمنطقة إبهام، وقصاصة أمان سفلية لشاشة iPhone كاملة.
  'fixed bottom-0 start-0 end-0 z-[var(--z-modal)] max-h-[85vh] overflow-y-auto',
  'rounded-t-xl border-t border-border bg-surface p-4 pb-[calc(16px+env(safe-area-inset-bottom))] shadow-2',
  // الديسكتوب: لوحة ممركزة أعلى الشاشة — `mx-auto` منطقية فلا يمين ولا يسار.
  'lg:bottom-auto lg:top-[12vh] lg:mx-auto lg:w-full lg:max-w-160 lg:rounded-b-xl lg:border lg:p-5',
].join(' ');

function CaptureDialog({
  open,
  onOpenChange,
  draft,
  onDraftChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  draft: string;
  onDraftChange: (text: string) => void;
}) {
  const isDesktop = useIsDesktop();
  const keyboardInset = useKeyboardInset(open && !isDesktop);

  // §5.6: الحركة كلها في `animate-panel-in` بـ base.css، وقاعدة
  // prefers-reduced-motion هناك تلغيها — فلا فحص في JavaScript.
  // (السابق: اللوحة scale 0.98→1 بـ dur4 والورقة تصعد بـ dur3؛ وreduced-motion
  // يبقي تغيّر الشفافية وحده في حدود 120ms.

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* حاجب ضوء لا لون هوية: التعتيم ليس من لوحة §5.2 ولا يُشتق منها. */}
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-overlay)] bg-black/50" />
        <Dialog.Content asChild aria-describedby={undefined}>
          {/*
            دخول اللوحة بـ CSS لا بمكتبة حركة: هذا المكوّن يغلّف الجذر في `__root.tsx`
            فيدخل الحزمة الابتدائية، واستيراد `motion` فيه كان يجرّ 31KB gz قبل أن
            يرى المالك حرفًا (ميزانية §7). المكتبة تبقى لشاشات المزايا حيث تُحمَّل
            مع chunk مسارها. الصنف `animate-panel-in` في base.css.
          */}
          <div
            style={keyboardInset > 0 ? { bottom: keyboardInset } : undefined}
            className={`${CONTENT_CLASS} animate-panel-in`}
          >
            <Dialog.Title className="sr-only">قيد سريع</Dialog.Title>
            <div aria-hidden="true" className="mx-auto mb-3 h-1 w-10 rounded-full bg-border lg:hidden" />
            <Suspense fallback={<div className="min-h-24" aria-hidden="true" />}>
              <CaptureComposer
                variant={isDesktop ? 'palette' : 'sheet'}
                draft={draft}
                onDraftChange={onDraftChange}
                onClose={() => onOpenChange(false)}
              />
            </Suspense>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
